import { useEffect, useRef, useState } from "react";
import ArrowToolButton from "./ArrowToolButton";
import CalloutToolButton from "./CalloutToolButton";
import CloudToolButton from "./CloudToolButton";
import EllipseToolButton from "./EllipseToolButton";
import HighlightToolButton from "./HighlightToolButton";
import ImageToolButton from "./ImageToolButton";
import PenToolButton from "./PenToolButton";
import PolylineToolButton from "./PolylineToolButton";
import RedactToolButton from "./RedactToolButton";
import RectCalloutToolButton from "./RectCalloutToolButton";
import RectToolButton from "./RectToolButton";
import SelectToolButton from "./SelectToolButton";
import StampToolButton, { STAMP_TOOL_ID } from "./StampToolButton";
import TextToolButton from "./TextToolButton";

const SHAPE_TOOL_IDS = [
  "rectangle",
  "ellipse",
  "cloud",
  "triangle",
  "line",
  "cross",
];

const MarkupToolButtons = ({
  activeTool,
  onSelectTool,
  onImageToolRequested,
  stampPresets,
  stampPreset,
  onSelectStampPreset,
  disabled = false,
  vertical = false,
}) => {
  const [isShapesOpen, setIsShapesOpen] = useState(false);
  const [isStampsOpen, setIsStampsOpen] = useState(false);
  const shapeButtonRef = useRef(null);
  const stampButtonRef = useRef(null);
  const [shapeFlyoutTop, setShapeFlyoutTop] = useState(0);
  const [stampFlyoutTop, setStampFlyoutTop] = useState(0);
  const isShapeActive = SHAPE_TOOL_IDS.includes(activeTool);
  const showShapes = isShapesOpen;
  const showStamps = isStampsOpen;

  const shapeGlyph =
    activeTool === "rectangle"
      ? "▭"
      : activeTool === "ellipse"
        ? "◯"
        : activeTool === "cloud"
          ? "☁"
          : activeTool === "triangle"
            ? "△"
            : activeTool === "line"
              ? "╱"
              : activeTool === "cross"
                ? "✕"
                : "◼";

  const shapeButtonClass = `btn ${showShapes || isShapeActive ? "btn-dark" : "btn-outline-dark"}`;

  useEffect(() => {
    setIsShapesOpen(false);
    setIsStampsOpen(false);
  }, [activeTool]);

  const closeFlyouts = () => {
    setIsShapesOpen(false);
    setIsStampsOpen(false);
  };

  const handleToolSelect = (toolId) => {
    onSelectTool(toolId);
    closeFlyouts();
  };

  const handleShapeToggle = () => {
    setIsStampsOpen(false);
    if (vertical && shapeButtonRef.current) {
      const button = shapeButtonRef.current;
      setShapeFlyoutTop(button.offsetTop + button.offsetHeight / 2);
    }
    setIsShapesOpen((prev) => !prev);
  };

  const handleShapeSelect = (toolId) => {
    handleToolSelect(toolId);
  };

  const handleStampToggle = () => {
    setIsShapesOpen(false);
    if (vertical && stampButtonRef.current) {
      const button = stampButtonRef.current;
      setStampFlyoutTop(button.offsetTop + button.offsetHeight / 2);
    }
    setIsStampsOpen((prev) => !prev);
  };

  const handleStampSelect = (preset) => {
    onSelectStampPreset?.(preset);
    handleToolSelect(STAMP_TOOL_ID);
  };

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        flexDirection: vertical ? "column" : "row",
        alignItems: "stretch",
        ...(disabled ? { opacity: 0.55, pointerEvents: "none" } : {}),
      }}
      aria-label="Markup tools"
      aria-disabled={disabled}
    >
      <div
        className={
          vertical
            ? "btn-group-vertical btn-group-sm"
            : "btn-group btn-group-sm"
        }
        role="group"
        aria-label="Markup tools"
      >
        <SelectToolButton activeTool={activeTool} onSelectTool={handleToolSelect} />
        <ImageToolButton
          activeTool={activeTool}
          onSelectTool={handleToolSelect}
          onPickImage={onImageToolRequested}
        />
        <PenToolButton activeTool={activeTool} onSelectTool={handleToolSelect} />
        <ArrowToolButton activeTool={activeTool} onSelectTool={handleToolSelect} />
        <button
          ref={shapeButtonRef}
          type="button"
          className={shapeButtonClass}
          onClick={handleShapeToggle}
          title="Shapes"
          aria-label="Shapes"
          aria-expanded={showShapes}
        >
          {shapeGlyph}
        </button>
        <TextToolButton activeTool={activeTool} onSelectTool={handleToolSelect} />
        <CalloutToolButton
          activeTool={activeTool}
          onSelectTool={handleToolSelect}
        />
        <RectCalloutToolButton
          activeTool={activeTool}
          onSelectTool={handleToolSelect}
        />
        <PolylineToolButton
          activeTool={activeTool}
          onSelectTool={handleToolSelect}
        />
        <RedactToolButton activeTool={activeTool} onSelectTool={handleToolSelect} />
        <HighlightToolButton
          activeTool={activeTool}
          onSelectTool={handleToolSelect}
        />
        <StampToolButton
          activeTool={activeTool}
          stampPreset={stampPreset}
          onTogglePresets={handleStampToggle}
          isOpen={showStamps}
          buttonRef={stampButtonRef}
        />
      </div>

      <div
        className={vertical ? "btn-group-vertical btn-group-sm" : "btn-group btn-group-sm"}
        role="group"
        aria-label="Stamp presets"
        style={
          vertical
            ? {
                position: "absolute",
                left: "calc(100% + 8px)",
                top: `${stampFlyoutTop}px`,
                transform: showStamps
                  ? "translate(0, -50%)"
                  : "translate(-10px, -50%)",
                opacity: showStamps ? 1 : 0,
                visibility: showStamps ? "visible" : "hidden",
                pointerEvents: showStamps ? "auto" : "none",
                transition: "opacity 140ms ease, transform 140ms ease",
                zIndex: 1510,
                padding: "10px",
                borderRadius: "10px",
                gap: "8px",
                minWidth: "176px",
                background:
                  "linear-gradient(160deg, rgba(58, 58, 58, 0.94) 0%, rgba(0, 93, 133, 0.9) 58%, rgba(156, 197, 202, 0.78) 100%)",
                border: "1px solid rgba(242, 242, 242, 0.24)",
                boxShadow: "0 10px 26px rgba(58, 58, 58, 0.3)",
                backdropFilter: "blur(6px)",
              }
            : {
                position: "absolute",
                left: "calc(100% + 6px)",
                top: 0,
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(132px, 1fr))",
                gap: "8px",
                minWidth: "288px",
                opacity: showStamps ? 1 : 0,
                visibility: showStamps ? "visible" : "hidden",
                pointerEvents: showStamps ? "auto" : "none",
                transform: showStamps ? "translateX(0)" : "translateX(-10px)",
                transition: "opacity 140ms ease, transform 140ms ease",
                background:
                  "linear-gradient(160deg, rgba(58, 58, 58, 0.94) 0%, rgba(0, 93, 133, 0.9) 58%, rgba(156, 197, 202, 0.78) 100%)",
                border: "1px solid rgba(242, 242, 242, 0.24)",
                boxShadow: "0 10px 26px rgba(58, 58, 58, 0.3)",
                borderRadius: "10px",
                padding: "10px",
                zIndex: 1510,
              }
        }
      >
        {(stampPresets || []).map((preset) => (
          <button
            key={preset}
            type="button"
            className={`btn ${stampPreset === preset ? "btn-dark" : "btn-outline-dark"}`}
            title={preset}
            aria-label={`Select ${preset} stamp`}
            onClick={() => handleStampSelect(preset)}
            style={{ minWidth: vertical ? "154px" : "132px", whiteSpace: "nowrap" }}
          >
            {preset}
          </button>
        ))}
      </div>

      <div
        className="btn-group btn-group-sm"
        role="group"
        aria-label="Shape tools"
        style={
          vertical
            ? {
                position: "absolute",
                left: "calc(100% + 8px)",
                top: `${shapeFlyoutTop}px`,
                transform: showShapes
                  ? "translate(0, -50%)"
                  : "translate(-10px, -50%)",
                opacity: showShapes ? 1 : 0,
                visibility: showShapes ? "visible" : "hidden",
                pointerEvents: showShapes ? "auto" : "none",
                transition: "opacity 140ms ease, transform 140ms ease",
                zIndex: 1510,
                padding: "8px",
                borderRadius: "10px",
                gap: "8px",
                background:
                  "linear-gradient(160deg, rgba(58, 58, 58, 0.94) 0%, rgba(0, 93, 133, 0.9) 58%, rgba(156, 197, 202, 0.78) 100%)",
                border: "1px solid rgba(242, 242, 242, 0.24)",
                boxShadow: "0 10px 26px rgba(58, 58, 58, 0.3)",
                backdropFilter: "blur(6px)",
              }
            : {
                marginLeft: "6px",
                opacity: showShapes ? 1 : 0,
                visibility: showShapes ? "visible" : "hidden",
                pointerEvents: showShapes ? "auto" : "none",
                transform: showShapes ? "translateX(0)" : "translateX(-10px)",
                transition: "opacity 140ms ease, transform 140ms ease",
                background:
                  "linear-gradient(160deg, rgba(58, 58, 58, 0.94) 0%, rgba(0, 93, 133, 0.9) 58%, rgba(156, 197, 202, 0.78) 100%)",
                border: "1px solid rgba(242, 242, 242, 0.24)",
                boxShadow: "0 10px 26px rgba(58, 58, 58, 0.3)",
                borderRadius: "10px",
                padding: "6px",
              }
        }
      >
        <RectToolButton
          activeTool={activeTool}
          onSelectTool={handleShapeSelect}
        />
        <EllipseToolButton
          activeTool={activeTool}
          onSelectTool={handleShapeSelect}
        />
        <CloudToolButton
          activeTool={activeTool}
          onSelectTool={handleShapeSelect}
        />
        <button
          type="button"
          className={`btn ${activeTool === "triangle" ? "btn-dark" : "btn-outline-dark"}`}
          title="Triangle"
          aria-label="Triangle"
          onClick={() => handleShapeSelect("triangle")}
        >
          △
        </button>
        <button
          type="button"
          className={`btn ${activeTool === "line" ? "btn-dark" : "btn-outline-dark"}`}
          title="Line"
          aria-label="Line"
          onClick={() => handleShapeSelect("line")}
        >
          ╱
        </button>
        <button
          type="button"
          className={`btn ${activeTool === "cross" ? "btn-dark" : "btn-outline-dark"}`}
          title="Cross"
          aria-label="Cross"
          onClick={() => handleShapeSelect("cross")}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default MarkupToolButtons;
